import React, { useMemo, useEffect, useReducer, useCallback } from 'react';
import algoliasearch from 'algoliasearch';
import searchReducer, {
  initialSearchState,
  UPDATE_QUERY_TEXT,
  POPULATE_SEARCH_RESULTS,
  UPDATE_ACTIVE_TAB_FILTER,
  UPDATE_ENGAGED_STATE,
  UPDATE_ACTIVE_RESULT_INDEX
} from '../ducks/search';
import { SearchResultsUtil } from '../utils/search_results_util';
import { StringConstantsMixin } from '../application/javascripts/react_components/mixins/shared/string_constants_mixin';

const NUM_ALGOLIA_RESULTS = 50;
const DEFAULT_RADIUS = 48270; // 30 miles in m
const algoliaRef = algoliasearch(StringConstantsMixin.constants.ALGOLIA.ACCOUNT, StringConstantsMixin.constants.ALGOLIA.API_KEY);
const algoliaSearch = algoliaRef.initIndex(StringConstantsMixin.constants.ALGOLIA.INDEX);

const useSearch = ({ latLng, languageRegionCode, enableClickTracking }) => {
  const [state, dispatch] = useReducer(searchReducer, initialSearchState);

  const handleResults = content => {
    const { queryID, hits } = content;
    const results = hits.map(hit => {
      return SearchResultsUtil.formatSearchResult(hit, languageRegionCode);
    });

    dispatch({ type: POPULATE_SEARCH_RESULTS, payload: { results, queryID } });
  };

  const getFacetFilters = useCallback(() => {
    const { key } = state.filters[state.activeFilterIndex];

    return key === 'all' ? ['type:-track', 'type:-map'] : `type:${key}`;
  }, [state.activeFilterIndex]);

  const handleLatLngSearch = facetFilters => {
    algoliaSearch
      .search('*', {
        hitsPerPage: NUM_ALGOLIA_RESULTS,
        aroundLatLng: latLng.join(','),
        aroundRadius: DEFAULT_RADIUS,
        facets: ['type', 'difficulty_rating'],
        facetFilters,
        clickAnalytics: enableClickTracking
      })
      .then(handleResults)
      .catch(err => console.error('useSearch hook error', err));
  };

  const handleSearch = facetFilters => {
    algoliaSearch
      .search(state.query, {
        hitsPerPage: NUM_ALGOLIA_RESULTS,
        getRankingInfo: 1,
        facets: ['type', 'difficulty_rating'],
        facetFilters,
        clickAnalytics: enableClickTracking
      })
      .then(handleResults)
      .catch(err => console.error('useSearch hook error', err));
  };

  useEffect(() => {
    if (!state.engaged && state.query.trim().length === 0) {
      return;
    }

    const facetFilters = getFacetFilters();

    if (state.query.trim().length === 0 && latLng) {
      handleLatLngSearch(facetFilters);
      return;
    }

    handleSearch(facetFilters);
  }, [state.query, state.activeFilterIndex, state.engaged]);

  return useMemo(() => {
    const updateQuery = payload => {
      dispatch({ type: UPDATE_QUERY_TEXT, payload });
    };

    const updateActiveTabFilter = (e, payload) => {
      e.preventDefault();
      _gaq.push(['_trackEvent', 'Search tab switch', state.filters[payload].key]);
      dispatch({ type: UPDATE_ACTIVE_TAB_FILTER, payload });
    };

    const updateActiveIndexResult = idx => {
      dispatch({ type: UPDATE_ACTIVE_RESULT_INDEX, payload: idx });
    };

    const toggleEngaged = payload => {
      dispatch({ type: UPDATE_ENGAGED_STATE, payload });
    };

    return { searchState: state, updateQuery, updateActiveTabFilter, toggleEngaged, updateActiveIndexResult };
  }, [state]);
};

export default useSearch;
